import {useState} from 'react'
import {dialogApi} from '../../../../../api/dialogApi'
import {getHexFromColorText, getTimePassed} from '../../../../../utils'
import {DIALOG_AVATAR_IMAGE_URL} from '../../../../../constants/dialogConstants'
import {cx} from '../../../../../utils/classNameUtil'
import {useWuShowToast, WuButton, WuIcon} from '@npm-questionpro/wick-ui-lib'
import {useParams} from 'react-router'
import {IRestrictedUser} from '../../../../../types'
import {BaseModal} from '../../../../../components/BaseModal/BaseModal'
import {ConfirmModalContent} from '../../../../../components/ConfirmModalContent'
import styles from './FlaggedResponseCard.module.scss'
import {useWt} from '@npm-questionpro/wick-ui-lib'
import {GLOBAL} from '../../../../../Translation/English/global'
import {DIALOG} from '../../../../../Translation/English/dialog'
import type {IFlaggedResponseItem} from '../../../../../types/dialogTypes'

interface IFlaggedResponseCardProps {
  data: IFlaggedResponseItem
  type: string
}

export const FlaggedResponseCard: React.FC<IFlaggedResponseCardProps> = ({
  data,
  type,
}) => {
  const wt = useWt()
  const {showToast} = useWuShowToast()
  const updateAvatarStatusMutation = dialogApi.useUpdateDialogUserAvatarStatus()
  const patchFlaggedResponseMutation = dialogApi.usePatchFlaggedResponse()
  const patchFlaggedRepliesMutation = dialogApi.usePatchFlaggedReplies()
  const deleteDialogResponseMutation = dialogApi.useDeleteDialogResponse()
  const deleteReplyMutation = dialogApi.useDeleteDialogResponseReply()
  const {dialogId} = useParams<{dialogId: string}>()
  const {data: dialogDetails} = dialogApi.useDialogDetails(dialogId ?? '')
  const [isShowDeleteModal, setIsShowDeleteModal] = useState<boolean>(false)
  const [deleteModalFooter, setDeleteModalFooter] =
    useState<React.ReactNode>(null)
  const isDeleting =
    type === 'response'
      ? deleteDialogResponseMutation.isPending
      : deleteReplyMutation.isPending

  const restrictUser = async (): Promise<void> => {
    try {
      await updateAvatarStatusMutation.mutateAsync({
        id: data.dialogUserAvtarId ?? '',
        isRestricted: true,
        dialogId,
      })
    } catch {
      showToast({message: wt(DIALOG.failedToRestrictUser), variant: 'error'})
    }
  }

  const updateItem = async (): Promise<void> => {
    try {
      if (type === 'response') {
        await patchFlaggedResponseMutation.mutateAsync({
          id: String(data.id),
          status: 'Approved',
          dialogId,
        })
      } else {
        await patchFlaggedRepliesMutation.mutateAsync({
          id: String(data.id),
          status: 'Approved',
          dialogId,
        })
      }
    } catch {
      showToast({message: wt(DIALOG.failedToUpdateItem), variant: 'error'})
    }
  }

  const confirmDeleteItem = async (): Promise<void> => {
    try {
      if (type === 'response') {
        await deleteDialogResponseMutation.mutateAsync({
          id: String(data.id),
          dialogId,
        })
      } else {
        await deleteReplyMutation.mutateAsync({id: String(data.id), dialogId})
      }
      setIsShowDeleteModal(false)
    } catch {
      showToast({message: wt(DIALOG.failedToDeleteItem), variant: 'error'})
    }
  }

  const isUserAlreadyRestricted = (responseUserName: string): boolean => {
    if (!Array.isArray(dialogDetails?.restrictedUsers)) return false
    return dialogDetails.restrictedUsers.some(
      (user: IRestrictedUser) => user.username === responseUserName,
    )
  }

  return (
    <div className={styles.flaggedRespCard} key={data.id}>
      <div className={styles.row}>
        <div className={styles.userDetails}>
          <div
            className={cx(
              styles.avatar,
              data.isModerator && styles.moderatorBg,
            )}
          >
            <img
              src={`${DIALOG_AVATAR_IMAGE_URL}${data.dialogUserAvtar.username}`}
              alt="coverImage"
              width={'100%'}
              height={'100%'}
              loading="lazy"
              className={styles.avatar}
              style={{
                backgroundColor: getHexFromColorText(
                  (data.dialogUserAvtar.username ?? '').split(' ')[0],
                ),
              }}
            />
          </div>
          <div className={styles.username}>
            {data.isModerator
              ? wt(DIALOG.moderator)
              : data.dialogUserAvtar.username}
          </div>
          <div className={styles.timestamp}>
            {data.createdAt ? getTimePassed(data.createdAt) : ''}
          </div>
        </div>
        {!isUserAlreadyRestricted(data.dialogUserAvtar.username ?? '') && (
          <WuIcon
            icon="wm-person-cancel"
            className={styles.restrictBtn}
            onClick={restrictUser}
          />
        )}
      </div>
      <div className={styles.text}>{data.text}</div>
      <div className={styles.footer}>
        <div className={styles.reason}>
          {wt(DIALOG.flaggedByAI)} {data.category}
        </div>
        <div className={styles.actions}>
          <WuButton
            Icon={<span className="wm-visibility-off" />}
            variant="secondary"
            onClick={updateItem}
          >
            {wt(GLOBAL.unhide)}
          </WuButton>
          <WuButton
            Icon={<span className="wm-delete" />}
            variant="primary"
            color="error"
            onClick={() => setIsShowDeleteModal(true)}
          >
            {wt(GLOBAL.delete)}
          </WuButton>
        </div>
      </div>
      <BaseModal
        visible={isShowDeleteModal}
        headerTitle={`${wt(GLOBAL.delete)} ${
          type === 'response' ? wt(DIALOG.response) : wt(DIALOG.reply)
        }`}
        onCloseModal={() => setIsShowDeleteModal(false)}
        isLoading={isDeleting}
        maxWidth="600px"
        variant="critical"
        footer={deleteModalFooter}
      >
        <ConfirmModalContent
          isLoading={isDeleting}
          description={`${wt(GLOBAL.deleteItemText)} ${
            type === 'response' ? wt(DIALOG.response) : wt(DIALOG.reply)
          }?`}
          handleConfirm={confirmDeleteItem}
          handleCancel={() => setIsShowDeleteModal(false)}
          setContent={setDeleteModalFooter}
          isDestructive
        />
      </BaseModal>
    </div>
  )
}
